// src/components/common/ConfirmReasonPicker.js
//
// Tappable reasons for a confirm that needs one (a far backdate, an
// out-of-window invoice date).
//
// Renders the app's shared flat pick list (`InlineRadioGroup`: flat rows, radio
// on the right, filled radio + blue label on the picked row, no box per row) -
// the same list Reschedule uses. Inventing a second reason UI is exactly the
// drift this repo keeps paying for. "Other..." keeps the free-text path
// (ConfirmFreeTextField) exactly as before.

import { useIntl } from 'react-intl';
import { CalendarClock, MessageSquareWarning, PencilLine, MoreHorizontal } from 'lucide-react';
import { foundation } from '../../ui/tokens';
import InlineRadioGroup from './InlineRadioGroup';

const { color, fontSize, fontFamily, fontWeight } = foundation;

const ICONS = {
  calendar: CalendarClock,
  dispute: MessageSquareWarning,
  correction: PencilLine,
};

const OTHER = 'other';
const LABEL_ID = 'confirm-reason-label';
const iconNode = (Icon) => <Icon size={16} strokeWidth={2} aria-hidden style={{ flexShrink: 0 }} />;

export const REASON_LABEL_ID = LABEL_ID;

export default function ConfirmReasonPicker({
  presets,
  value,
  freeform,
  disabled = false,
  onPick,
  onOther,
  /** False when the caller already renders the field's label (one owner for the prompt). */
  showPrompt = true,
}) {
  const intl = useIntl();

  const labelOf = (preset) => intl.formatMessage({ id: preset.labelId, defaultMessage: preset.defaultMessage });
  const options = [
    ...presets.map((preset) => {
      const Icon = ICONS[preset.icon];
      return { value: preset.icon, label: labelOf(preset), leading: Icon ? iconNode(Icon) : undefined };
    }),
    { value: OTHER, label: intl.formatMessage({ id: 'confirm.reason.other', defaultMessage: 'Other…' }), leading: iconNode(MoreHorizontal) },
  ];
  const picked = freeform ? OTHER : (presets.find((preset) => labelOf(preset) === value)?.icon ?? null);

  return (
    <>
      {showPrompt ? (
      <div
        id={LABEL_ID}
        style={{
          fontSize: fontSize.xs,
          fontWeight: fontWeight.semibold,
          fontFamily: fontFamily.sans,
          color: color.neutral.textSecondary,
        }}
      >
        {intl.formatMessage({ id: 'confirm.reason.selectPrompt', defaultMessage: 'Select a reason:' })}
      </div>
      ) : null}
      <InlineRadioGroup
        value={picked}
        options={options}
        disabled={disabled}
        labelledBy={LABEL_ID}
        testIdPrefix="confirm-reason-list"
        optionTestIdPrefix="confirm-reason"
        onChange={(next) => {
          if (next === OTHER) { onOther(); return; }
          const preset = presets.find((p) => p.icon === next);
          if (preset) onPick(labelOf(preset));
        }}
      />
    </>
  );
}
